import React from 'react';
import styled, { useTheme } from 'styled-components';
import { ThemeColors } from 'constants/Colors';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

const RingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  border-right: 1px solid ${({ theme }) => theme.colors.border.light};
`;

interface AssignmentProgressRingProps {
  totalAssignments: number;
  assignedCount: number;
  plannedCount: number;
  /** Optional tooltip override. */
  tooltip?: string;
  /** Optional empty-state tooltip override. */
  emptyTooltip?: string;
  /** Optional icon to display below the ring. */
  icon?: IconDefinition;
}

const RADIUS = 15;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const AssignmentProgressRing: React.FC<AssignmentProgressRingProps> = ({
  totalAssignments,
  assignedCount,
  plannedCount,
  tooltip,
  emptyTooltip,
  icon,
}) => {
  const theme = useTheme() as { colors: ThemeColors };

  const total = totalAssignments || 1;
  const progressCount = assignedCount + plannedCount;
  const progressPct = Math.min(Math.round((progressCount / total) * 100), 100);
  const allAssigned = assignedCount === totalAssignments;

  const isEmpty = totalAssignments === 0;

  const color = isEmpty
    ? theme.colors.foreground.tertiary
    : allAssigned
      ? theme.colors.foreground.good
      : theme.colors.foreground.tint;

  // strokeDashoffset: full circumference = 0% filled, 0 = 100% filled
  // We subtract the filled portion from the circumference
  const offset = CIRCUMFERENCE - (progressPct / 100) * CIRCUMFERENCE;

  const tooltipText = isEmpty
    ? (emptyTooltip ?? 'Inga bokade uppdrag')
    : (tooltip ?? `Ni har planerat ${progressPct}% av dagens bokade uppdrag`);

  return (
    <RingContainer title={tooltipText}>
      <svg viewBox="0 0 36 36" width={48} height={48}>
        {/* Background track */}
        <circle
          cx="18"
          cy="18"
          r={RADIUS}
          fill="none"
          stroke={theme.colors.background.tertiary}
          strokeWidth="3"
        />
        {/* Progress arc */}
        {!isEmpty && progressPct > 0 && (
          <circle
            cx="18"
            cy="18"
            r={RADIUS}
            fill="none"
            stroke={color}
            strokeWidth="3"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={offset}
            transform="rotate(-90 18 18)"
            style={{ transition: 'stroke-dashoffset 0.4s ease' }}
          />
        )}
        {icon ? (
          <>
            {/* Icon in upper half */}
            <svg
              x={18 - 5.5}
              y={14 - 5.5}
              width="11"
              height="11"
              viewBox={`0 0 ${icon.icon[0]} ${icon.icon[1]}`}
            >
              <path fill="black" d={typeof icon.icon[4] === 'string' ? icon.icon[4] : icon.icon[4][0]} />
            </svg>
            {/* Percentage text in lower half */}
            <text
              x="18"
              y="23"
              textAnchor="middle"
              dominantBaseline="central"
              fontSize="7"
              fontWeight="bold"
              fill="black"
            >
              {isEmpty ? '–' : `${progressPct}%`}
            </text>
          </>
        ) : (
          <text
            x="18"
            y="18"
            textAnchor="middle"
            dominantBaseline="central"
            fontSize="8"
            fontWeight="bold"
            fill="white"
          >
            {isEmpty ? '–' : `${progressPct}%`}
          </text>
        )}
      </svg>
    </RingContainer>
  );
};

export default AssignmentProgressRing;
